import axiosInstance from '$api/index';
import {ApiRoute} from '$constants/api';
import {
  PaymentListOrderBy,
  PaymentStatus,
} from '$modules/frontdesk/Finance/PaymentList/constant';
import store from '$redux/store';
import {AxiosError} from 'axios';
import {Patient} from '~/modules/frontdesk/Finance/PaymentList/hooks';
import {dateToTimeShort, formatDate} from '~/utils/date';

export interface Payment extends Record<string, unknown> {
  id: number;
  patientName: string;
  amount: number;
  paymentIntentId: string;
  status: string;
  paymentMethodType: string;
  invoiceNumber: string;
  billingDate: string;
  description: string;
}

export interface GetPaymentListResponseData {
  success: boolean;
  data: {
    payments: {
      id: number;
      patient_full_name: string;
      amount: string;
      payment_intent_id: string;
      status: string;
      payment_method: string;
      invoice_number: string;
      billing_date: string;
      description: string;
    }[];
    patient?: Patient;
    total_pages: number;
    total_records: number;
    all_payment_count: number;
    paid_payment_count: number;
    unpaid_payment_count: number;
    min_amount_range: number;
    max_amount_range: number;
  };
  message: string;
}

export interface GetPaymentListParams {
  search?: string;
  status?: PaymentStatus | string;
  orderBy?: PaymentListOrderBy;
  orderDirection?: 'asc' | 'desc';
  pageNumber?: number;
  perPage?: number;
  fromDate?: Date;
  toDate?: Date;
  patientId?: number;
  min_amount?: number;
  max_amount?: number;
  payment_method?: string[];
}

const DEFAULT_PAGE_SIZE = 10;
const DEFAULT_PAGE_NUMBER = 1;

const mapApiPaymentToPayment = (
  apiPayment: GetPaymentListResponseData['data']['payments'][0]
): Payment => {
  const formattedDate = formatDate(apiPayment.billing_date);
  const billingDate =
    apiPayment.status === 'paid'
      ? `${formattedDate} ${dateToTimeShort(apiPayment.billing_date)}`
      : formattedDate;

  return {
    id: apiPayment.id,
    patientName: apiPayment.patient_full_name,
    amount: parseFloat(apiPayment.amount),
    paymentIntentId: apiPayment.payment_intent_id,
    status: apiPayment.status,
    paymentMethodType: apiPayment.payment_method,
    invoiceNumber: apiPayment.invoice_number,
    billingDate,
    description: apiPayment.description,
  };
};

export async function getPaymentList(
  params: GetPaymentListParams = {}
): Promise<{
  payments: Payment[];
  totalPages: number;
  totalRecords: number;
  message: string;
  success: boolean;
  all_payment_count: number;
  paid_payment_count: number;
  unpaid_payment_count: number;
  patient?: Patient;
  min_amount_range: number;
  max_amount_range: number;
}> {
  const {
    search,
    status,
    orderBy,
    orderDirection,
    pageNumber = DEFAULT_PAGE_NUMBER,
    perPage = DEFAULT_PAGE_SIZE,
    fromDate,
    toDate,
    patientId,
    min_amount,
    max_amount,
    payment_method,
  } = params;

  const token = store.getState().auth.token;
  if (!token) {
    throw new Error('Authentication token is missing');
  }

  try {
    const response = await axiosInstance.get<GetPaymentListResponseData>(
      ApiRoute.Payments,
      {
        params: {
          search,
          status,
          order_by: orderBy,
          order_direction: orderDirection,
          page_no: pageNumber,
          per_page: perPage,
          from_date: fromDate?.toISOString(),
          to_date: toDate?.toISOString(),
          patient_id: patientId,
          min_amount,
          max_amount,
          payment_method,
        },
        headers: {
          Authorization: token,
        },
      }
    );

    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to fetch payments');
    }

    const responseData = response.data.data;
    const responsePayments = responseData?.payments || [];

    return {
      success: response.data.success,
      message: response.data.message,
      payments: responsePayments.map(mapApiPaymentToPayment),
      totalPages: responseData.total_pages,
      totalRecords: responseData.total_records,
      all_payment_count: responseData.all_payment_count,
      paid_payment_count: responseData.paid_payment_count,
      unpaid_payment_count: responseData.unpaid_payment_count,
      patient: response.data.data.patient,
      min_amount_range: responseData.min_amount_range,
      max_amount_range: responseData.max_amount_range,
    };
  } catch (error) {
    if (error instanceof AxiosError) {
      if (error.response) {
        throw new Error(
          `Payment list request failed with status ${error.response.status}: ${
            error.response.data?.message || error.message
          }`
        );
      } else if (error.request) {
        throw new Error('No response received from server');
      }
    }

    throw new Error('Failed to fetch payments: Unknown error occurred');
  }
}

export const deleteFinancialRecord = async (id: number) => {
  const state = store.getState();
  const token = state.auth.token;

  const response = await axiosInstance.delete(`${ApiRoute.Payments}/${id}`, {
    headers: {
      Authorization: token,
    },
  });

  return response.data;
};
